/**
 * PhotoGalleryPanel — vintage-polaroid gallery for a person, on the right-side
 * rail of PersonPage. Every photo is a polaroid card. Each card has:
 *   - the image (auto-rotates through multiple uploads)
 *   - "primary" tag on whichever photo represents this person on the tree
 *   - Bring it to life → opens preset picker, starts a Higgsfield generation
 *   - shows the resulting video when ready
 *   - actions: set primary, delete
 *
 * Tier gate: animation requires Family/Archive. Other tiers see the panel
 * but the Bring-to-life button opens an upgrade hint instead of a render.
 */
import { useEffect, useRef, useState } from 'react'
import { api } from '../../services/api.js'
import './PhotoGalleryPanel.css'

const GATED_TIERS = new Set(['researcher', 'family', 'archive'])
const POLL_MS = 5000

export default function PhotoGalleryPanel({ personId, canEdit, userTier }) {
  const [photos, setPhotos] = useState([])
  const [videos, setVideos] = useState([])   // array
  const [presets, setPresets] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [presetForPhoto, setPresetForPhoto] = useState(null) // photoId or null
  const [chosenPreset, setChosenPreset] = useState('portrait')
  const [busyPhotoId, setBusyPhotoId] = useState(null)
  const fileRef = useRef(null)
  const pollRef = useRef(null)

  const allowed = GATED_TIERS.has(userTier)
  const videoByPhoto = Object.fromEntries(videos.map(v => [v.source_photo_id, v]))
  const pendingIds = videos.filter(v => v.status === 'queued' || v.status === 'generating').map(v => v.id)

  // Initial load
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [pList, vList, pr] = await Promise.all([
          api.get(`/persons/${personId}/photos`),
          api.get(`/persons/${personId}/video`).catch(() => []),
          api.get(`/persons/${personId}/video/presets`).catch(() => ({ presets: [] })),
        ])
        if (cancelled) return
        setPhotos(pList || [])
        setVideos(Array.isArray(vList) ? vList : [])
        setPresets(pr.presets || [])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [personId])

  // Poll while any video is still cooking
  useEffect(() => {
    if (pendingIds.length === 0) return
    pollRef.current = setInterval(async () => {
      try {
        const vList = await api.get(`/persons/${personId}/video`)
        setVideos(Array.isArray(vList) ? vList : [])
      } catch {}
    }, POLL_MS)
    return () => clearInterval(pollRef.current)
  }, [pendingIds.length, personId])

  async function onUpload(e) {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setError(null); setUploading(true)
    try {
      for (const file of files) {
        const form = new FormData()
        form.append('photo', file)
        const row = await api.upload(`/persons/${personId}/photos`, form)
        setPhotos(prev => [row, ...prev])
      }
    } catch (err) {
      setError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  async function setPrimary(photoId) {
    try {
      await api.patch(`/persons/${personId}/photos/${photoId}/primary`, {})
      setPhotos(prev => prev.map(p => ({ ...p, is_primary: p.id === photoId })))
    } catch (err) {
      setError(err.message || 'Failed to set primary')
    }
  }

  async function removePhoto(photoId) {
    if (!confirm('Delete this photo? Any animation made from it will also be removed.')) return
    try {
      await api.delete(`/persons/${personId}/photos/${photoId}`)
      setPhotos(prev => prev.filter(p => p.id !== photoId))
      setVideos(prev => prev.filter(v => v.source_photo_id !== photoId))
    } catch (err) {
      setError(err.message || 'Delete failed')
    }
  }

  async function startAnimation(photoId) {
    if (!allowed) { setPresetForPhoto('upgrade'); return }
    setBusyPhotoId(photoId); setError(null)
    try {
      const row = await api.post(`/persons/${personId}/video`, {
        photo_id: photoId,
        preset: chosenPreset,
      })
      setVideos(prev => {
        const without = prev.filter(v => v.source_photo_id !== photoId)
        return [row, ...without]
      })
      setPresetForPhoto(null)
    } catch (err) {
      setError(err.message || 'Failed to start animation')
    } finally {
      setBusyPhotoId(null)
    }
  }

  async function deleteVideo(videoId, photoId) {
    if (!confirm('Remove this animation?')) return
    try {
      await api.delete(`/persons/${personId}/video/${videoId}`)
      setVideos(prev => prev.filter(v => v.id !== videoId))
    } catch (err) {
      setError(err.message || 'Failed to delete')
    }
  }

  if (loading) return null

  return (
    <aside className="polaroid-rail">
      <div className="polaroid-rail__header">
        <span className="polaroid-rail__title">Photos</span>
        {canEdit && (
          <>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : '+ Add photo'}
            </button>
            <input
              ref={fileRef}
              id="person-gallery-photo-input"
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={onUpload}
            />
          </>
        )}
      </div>

      {error && <div className="form-error polaroid-rail__error">{error}</div>}

      {photos.length === 0 ? (
        canEdit ? (
          <button
            type="button"
            className="polaroid-rail__empty"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            <div className="polaroid-rail__empty-icon">🖼️</div>
            <div className="polaroid-rail__empty-title">Add a photo</div>
            <div className="polaroid-rail__empty-hint">
              Upload portraits or family snapshots. Multiple allowed; pick which to feature on the tree.
            </div>
          </button>
        ) : (
          <div className="polaroid-rail__muted">No photos yet.</div>
        )
      ) : (
        <div className="polaroid-rail__grid">
          {photos.map((p, i) => {
            const vid = videoByPhoto[p.id]
            const animating = vid && (vid.status === 'queued' || vid.status === 'generating')
            const done = vid?.status === 'done' && vid.url
            const failed = vid?.status === 'failed'
            const rotate = (i % 5) - 2  // -2..+2 deg for a casually-stacked look
            return (
              <div key={p.id} className="polaroid-slot">
              <div className="polaroid" style={{ '--rot': `${rotate}deg` }}>
                <div className="polaroid__photo-wrap">
                  {done ? (
                    <AnimatedPortraitVideo
                      src={vid.url}
                      poster={p.photo_url}
                    />
                  ) : (
                    <img className="polaroid__media" src={p.photo_url} alt="" />
                  )}
                  {p.is_primary && <span className="polaroid__primary-chip">Primary</span>}
                  {animating && (
                    <div className="polaroid__overlay">
                      <div className="polaroid__spinner" />
                      <div className="polaroid__overlay-text">
                        {vid.status === 'queued' ? 'Queued…' : 'Animating…'}
                      </div>
                    </div>
                  )}
                  {done && <span className="polaroid__live-chip">LIVE</span>}
                </div>
                <div className="polaroid__caption">
                  {p.caption || (done ? presets.find(pr => pr.id === vid.preset)?.label : '')}
                </div>
                {canEdit && (
                  <div className="polaroid__actions">
                    {!done && !animating && (
                      <button
                        className="polaroid__action polaroid__action--primary"
                        onClick={() => setPresetForPhoto(allowed ? p.id : 'upgrade')}
                        disabled={busyPhotoId === p.id}
                      >
                        {allowed ? '✨ Bring it to life' : '🔒 Upgrade to animate'}
                      </button>
                    )}
                    {failed && (
                      <button className="polaroid__action" onClick={() => startAnimation(p.id)}>
                        Retry animation
                      </button>
                    )}
                    {done && (
                      <button className="polaroid__action" onClick={() => deleteVideo(vid.id, p.id)}>
                        Remove animation
                      </button>
                    )}
                    {!p.is_primary && (
                      <button className="polaroid__action" onClick={() => setPrimary(p.id)}>
                        Set as primary
                      </button>
                    )}
                    <button className="polaroid__action polaroid__action--danger" onClick={() => removePhoto(p.id)}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Preset picker modal */}
      {presetForPhoto && presetForPhoto !== 'upgrade' && (
        <div className="polaroid-modal-backdrop" onClick={() => setPresetForPhoto(null)}>
          <div className="polaroid-modal" onClick={e => e.stopPropagation()}>
            <div className="polaroid-modal__title">Bring this photo to life</div>
            <p className="polaroid-modal__subtitle">Pick an animation style. Usually takes 1–3 minutes to render.</p>
            <div className="polaroid-modal__presets">
              {presets.map(pr => (
                <label key={pr.id} className={`polaroid-preset ${chosenPreset === pr.id ? 'polaroid-preset--on' : ''}`}>
                  <input type="radio" name="preset" value={pr.id}
                    checked={chosenPreset === pr.id}
                    onChange={() => setChosenPreset(pr.id)} />
                  <div>
                    <div className="polaroid-preset__label">{pr.label}</div>
                    <div className="polaroid-preset__desc">{pr.description}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="polaroid-modal__actions">
              <button className="btn btn-primary btn-sm"
                disabled={busyPhotoId === presetForPhoto}
                onClick={() => startAnimation(presetForPhoto)}>
                {busyPhotoId === presetForPhoto ? 'Starting…' : 'Animate'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setPresetForPhoto(null)}
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade modal for tier-gated users */}
      {presetForPhoto === 'upgrade' && (
        <div className="polaroid-modal-backdrop" onClick={() => setPresetForPhoto(null)}>
          <div className="polaroid-modal" onClick={e => e.stopPropagation()}>
            <div className="polaroid-modal__title">Bring portraits to life</div>
            <p className="polaroid-modal__subtitle">
              Subtle head movements, soft smiles, wind catching a scarf — AI-rendered 10-second clips of your ancestors' portraits.
              Available on the <strong>Family</strong> and <strong>Archive</strong> plans.
            </p>
            <div className="polaroid-modal__actions">
              <button className="btn btn-ghost btn-sm" onClick={() => setPresetForPhoto(null)}
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}

// iOS Safari (iPad included) won't autoplay a <video> declared only with
// React props in some conditions — muted has to be set on the DOM element
// before metadata loads, and preload="auto" nudges it to actually fetch.
// We also imperatively call .play() on mount and swallow any rejection
// silently (happens on low-power mode / some older iPads — falls back to
// showing the poster frame, which is still the person's photo so nothing
// visually breaks).
function AnimatedPortraitVideo({ src, poster }) {
  const ref = useRef(null)
  useEffect(() => {
    const v = ref.current
    if (!v) return
    v.muted = true // guarantee muted before play attempt
    const tryPlay = () => v.play().catch(() => {})
    tryPlay()
    v.addEventListener('canplay', tryPlay, { once: true })
    return () => v.removeEventListener('canplay', tryPlay)
  }, [src])
  return (
    <video
      ref={ref}
      className="polaroid__media"
      src={src}
      poster={poster}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      disableRemotePlayback
    />
  )
}
